import { AccountInfo, CacheLookupPolicy, IPublicClientApplication } from "@azure/msal-browser";
import { loginRequest } from "./MsAuthConfig";

// Tenant Microsoft issues personal (consumer) accounts from. A personal and a work account
// can share the same e-mail address, so the address alone does not tell them apart.
const PERSONAL_ACCOUNT_TENANT_ID = '9188040d-6c67-4c5b-b112-36a304b66dad';

// Set when the user signs a Microsoft account out of WebTools. Microsoft keeps its own
// session for that account (cookies on login.microsoftonline.com / login.live.com, out of
// the app's reach - clearing the site data does not touch them) and would hand the same
// account straight back on the next sign-in. Until a Microsoft login succeeds, Microsoft is
// asked to let the user pick the account instead.
const SELECT_ACCOUNT_KEY = 'ms-select-account';
// Page the Microsoft login started from, so a failed login can return to it (the plugin
// WebView has no address bar or back button).
const LOGIN_PAGE_KEY = 'ms-login-page';
// When the cached account was last sent back to Microsoft for fresh tokens (sessionStorage,
// it only has to survive the redirect in this tab).
const RENEWAL_KEY = 'ms-account-renewal';
const RENEWAL_LOOP_WINDOW_MS = 60000;

export const authCallbackUri = () => window.location.protocol + '//' + window.location.host + '/auth-callback';

// The account a Microsoft login continues with. RedirectHandler takes it from the MSAL cache
// without asking Microsoft, so the login page shows exactly this one.
export function getMicrosoftLoginAccount(accounts: AccountInfo[]): AccountInfo | undefined {
    return accounts[0];
}

export function microsoftAccountKindKey(account: AccountInfo) {
    return account.tenantId === PERSONAL_ACCOUNT_TENANT_ID ? 'MicrosoftAccount.Personal' : 'MicrosoftAccount.Work';
}

// Drops the account from the MSAL cache only. Microsoft's own sign-out page is skipped: it
// returns only to a registered address and could leave the plugin WebView stranded there.
export async function signOutMicrosoftAccount(instance: IPublicClientApplication, account: AccountInfo) {
    localStorage.setItem(SELECT_ACCOUNT_KEY, 'true');
    await instance.logoutRedirect({ account, onRedirectNavigate: () => false });
}

export function startMicrosoftLogin(instance: IPublicClientApplication) {
    return instance.loginRedirect({
        scopes: loginRequest.scopes,
        redirectUri: authCallbackUri(),
        prompt: localStorage.getItem(SELECT_ACCOUNT_KEY) === 'true' ? 'select_account' : undefined,
    });
}

// Token for the cached account, from the cache or its refresh token only. The cache lives in
// localStorage and outlasts the refresh token (24 hours for a single-page app); MSAL would
// then try a hidden iframe, which returns to the address the app was opened at - not a
// registered redirect address - and gives up after 10 seconds with an error that does not
// ask for interaction. Failing straight away lets the caller renew the account instead.
export function acquireMicrosoftTokenSilent(instance: IPublicClientApplication, account: AccountInfo) {
    return instance.acquireTokenSilent({
        scopes: loginRequest.scopes,
        account: account,
        redirectUri: authCallbackUri(),
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
    });
}

// Sends the cached account back to Microsoft for fresh tokens - no prompt while Microsoft's
// own session lasts. Returns false when that has just been done and the tokens still cannot
// be read, so the caller reports the failure instead of redirecting in a loop.
export async function renewMicrosoftAccount(instance: IPublicClientApplication, account: AccountInfo) {
    const lastRenewal = Number(sessionStorage.getItem(RENEWAL_KEY));
    if (lastRenewal && Date.now() - lastRenewal < RENEWAL_LOOP_WINDOW_MS) {
        sessionStorage.removeItem(RENEWAL_KEY);
        return false;
    }
    sessionStorage.setItem(RENEWAL_KEY, Date.now().toString());
    await instance.acquireTokenRedirect({ scopes: loginRequest.scopes, account: account, redirectUri: authCallbackUri() });
    return true;
}

export function microsoftTokenAcquired() {
    sessionStorage.removeItem(RENEWAL_KEY);
}

// The server accepted the account, so Microsoft's session can be trusted again.
export function microsoftLoginSucceeded() {
    localStorage.removeItem(SELECT_ACCOUNT_KEY);
}

export function rememberMicrosoftLoginPage(path: string) {
    localStorage.setItem(LOGIN_PAGE_KEY, path);
}

export function getMicrosoftLoginPage() {
    return localStorage.getItem(LOGIN_PAGE_KEY) ?? '/';
}
